import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import setTextFixedFrameOperation from 'editor/src/store/design/operation/setTextFixedFrameOperation';
import getSelectedTextElementObject from 'editor/src/store/design/selector/getSelectedTextElementObject';
import { TextWrapping } from 'editor/src/store/design/types';
import isTextOverflowFramesAllowed from 'editor/src/store/editor/selector/isTextOverflowFramesAllowed';
import { canHaveFixedTextFrame } from 'editor/src/store/editorModules/warnings/utils/getTextOverflowWarning';
import applyFontStylesToSelectionOperation from 'editor/src/store/fonts/operation/applyFontStylesToSelectionOperation';
import getCurrentFontStylesProperty from 'editor/src/store/fonts/selector/getCurrentFontStylesProperty';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import { isEmbedded } from 'editor/src/store/watchers/useDisplayMode';

import Checkbox from '../Checkbox';
import IconInfo from '../Icon/IconInfo';
import SwitchControl from '../SwitchControl';
import WithTooltip from '../WithTooltip';

import styles from './index.module.scss';

function TextFittingControl() {
  const wrapping = useSelector((state) => getCurrentFontStylesProperty(state, 'wrapping')) ?? TextWrapping.Wrap;
  const lockBoxSize = useSelector((state) => !!getSelectedTextElementObject(state)?.extra?.lockBoxSize);
  const fixedFrame = useSelector((state) => !!getSelectedTextElementObject(state)?.extra?.fixedFrame);
  // GAPI-61892: same conditions as the overflow check, so the frame cannot be turned on
  // where it would have no effect — but one that is on can always be turned off
  const fixedFrameApplies = useSelector((state) => {
    const element = getSelectedTextElementObject(state);
    return !!element && canHaveFixedTextFrame(element);
  });
  // GAPI-61892: offered only where allowed (see isTextOverflowFramesAllowed), or outside the embedded editor on a
  // text that already has one, so the author can always turn it back off. A buyer cannot remove a frame the
  // template set unless the control is allowed for them.
  const showFixedFrame = useSelector(isTextOverflowFramesAllowed) || (!isEmbedded && fixedFrame);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onFitSwitch = useCallback(
    (on: boolean) => {
      // autofit shrinks the text into the frame, so the frame never overflows: a fixed frame has
      // nothing left to flag and is turned off with it
      if (on && fixedFrame) {
        dispatch(setTextFixedFrameOperation(false));
      }
      dispatch(
        applyFontStylesToSelectionOperation({
          wrapping: on ? TextWrapping.Fit : undefined,
        }),
      );
    },
    [wrapping, fixedFrame],
  );

  const onMultiLineSwitch = useCallback(
    (on: boolean) => {
      dispatch(
        applyFontStylesToSelectionOperation({
          wrapping: on ? TextWrapping.Fit : TextWrapping.FitOneLine,
        }),
      );
    },
    [wrapping],
  );

  const onFixedFrameChange = useCallback((on: boolean) => {
    dispatch(setTextFixedFrameOperation(on));
  }, []);

  const autoFitElt = useMemo(
    () => (
      <>
        {t('Autofit text')}
        <WithTooltip overlay={t('editor-autofit-tooltip')}>
          <IconInfo size="small" className="ml-1" />
        </WithTooltip>
      </>
    ),
    [],
  );

  const fixedFrameElt = useMemo(
    () => (
      <>
        {t('editor-text-fixed-frame-title')}
        <WithTooltip overlay={t('editor-text-fixed-frame-tooltip')}>
          <IconInfo size="small" className="ml-1" />
        </WithTooltip>
      </>
    ),
    [],
  );

  const isAutofit = wrapping === TextWrapping.Fit || wrapping === TextWrapping.FitOneLine;
  // a frame that is on can always be turned back off
  const fixedFrameDisabled = !fixedFrame && (!fixedFrameApplies || wrapping !== TextWrapping.Wrap);

  return (
    <>
      <SwitchControl title={autoFitElt} on={isAutofit} onSwitch={onFitSwitch} disabled={lockBoxSize} />
      <Checkbox
        className={styles.option}
        disabled={lockBoxSize || !isAutofit}
        on={wrapping !== TextWrapping.FitOneLine}
        label={t('editor-allow-multiline')}
        onChange={onMultiLineSwitch}
      />
      {showFixedFrame && (
        <div className={cn(styles.section, { [styles.disabled]: fixedFrameDisabled })}>
          <SwitchControl
            className="cy-fixed-frame-switch"
            title={fixedFrameElt}
            on={fixedFrame}
            onSwitch={onFixedFrameChange}
            disabled={fixedFrameDisabled}
          />
          {!fixedFrame && isAutofit && <div className={styles.hint}>{t('editor-text-fixed-frame-autofit-hint')}</div>}
        </div>
      )}
    </>
  );
}

export default React.memo(TextFittingControl);
